import { Skeleton } from './Skeleton.js'
import { LoadFailure } from './LoadFailure.js'
import type { ReactNode } from 'react'
import { EndpointHint } from './EndpointHint.js'

/** A titled block inside a detail view. */
export function DetailSection({ title, endpoint, children }: { title: string; endpoint?: string; children: ReactNode }) {
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2 border-b border-marine/15 pb-1">
        <h3 className="text-[10px] font-bold uppercase tracking-wide text-marine">{title}</h3>
        {/* The call behind the section, since this app exists to show what each endpoint returns - to a SeeWhoCares admin; a sales login sees none. */}
        {endpoint && <EndpointHint>{endpoint}</EndpointHint>}
      </div>
      {children}
    </section>
  )
}

export interface DetailField {
  label: string
  value: ReactNode
  /** For identifiers and other values meant to be read exactly or copied. */
  mono?: boolean
}

/** Label and value pairs in a two-column grid. */
export function DetailFields({ fields }: { fields: readonly DetailField[] }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      {fields.map((field) => (
        <div key={field.label} className="min-w-0">
          <dt className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{field.label}</dt>
          <dd className={`text-xs text-ink break-words ${field.mono ? 'font-mono select-all' : ''}`}>{field.value}</dd>
        </div>
      ))}
    </dl>
  )
}

interface QueryLike<T> {
  isPending: boolean
  isError: boolean
  error: unknown
  data: T | undefined
  refetch?: () => Promise<unknown>
  isFetching?: boolean
}

/**
 * Renders one query of a detail view on its own terms: a section that is
 * loading, failed, or simply has nothing for this record does not hold up
 * or blank out the sections around it. A 404 is shown as `missing` rather
 * than as a failure, because here it means "not set up yet".
 */
export function QueryBlock<T>({ query, missing, children }: { query: QueryLike<T>; missing: string; children: (data: T) => ReactNode }) {
  if (query.isPending) {
    return <Skeleton />
  }
  if (query.isError || query.data === undefined) {
    const status = (query.error as { status?: number } | null)?.status
    if (status === 404) {
      return <div className="text-xs text-ink-mid">{missing}</div>
    }
    return <LoadFailure size="xs" message="Could not load this." onRetry={() => void query.refetch?.()} retrying={query.isFetching ?? false} />
  }
  return <>{children(query.data)}</>
}

export function formatDate(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}
